import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../auth';
import './Timesheet.css';

// Detailed hours view (Sprint 24.3: shown inside the staff portal's Home — there is no separate Timesheet page and no CSV export). Pulls /api/me/hours which returns the week summary
// plus raw entries that INTERSECT the requested week (so cross-week shifts
// remain visible). Daily breakdown groups entries client-side; entries that
// cross midnight are split per calendar day; live segments contribute their
// elapsed hours to the day total even before clock-out.

const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';

const fmtHours = (h) => {
  if (h == null || isNaN(h) || h === 0) return '0h';
  if (h % 1 === 0)                       return `${h}h`;
  return `${h.toFixed(1)}h`;
};

const fmtHoursMinutes = (h) => {
  if (h == null || isNaN(h) || h === 0) return '0h 0m';
  const hours = Math.floor(h);
  const mins  = Math.round((h - hours) * 60);
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours)         return `${hours}h`;
  return `${mins}m`;
};

const localDayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const getMondayISO = (d = new Date()) => {
  const date = new Date(d);
  const dow  = date.getDay();
  const off  = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + off);
  return localDayKey(date);
};

// Walk an entry day-by-day in local time and emit one segment per calendar
// day. Each segment carries its own start/end (clamped to midnight) and its
// own hours (always computed — live segments get elapsed time too, so day
// totals reflect the work that actually happened).
const splitEntryByDay = (entry) => {
  const start    = new Date(entry.clock_in_time);
  const realEnd  = entry.clock_out_time ? new Date(entry.clock_out_time) : new Date();
  const isLive   = !entry.clock_out_time;
  if (realEnd <= start) return [];

  const segs = [];
  let cursor  = new Date(start);
  while (cursor < realEnd) {
    const dayKey       = localDayKey(cursor);
    const nextMidnight = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1, 0, 0, 0, 0);
    const segEndDate   = nextMidnight < realEnd ? nextMidnight : realEnd;
    const isLastSeg    = segEndDate.getTime() === realEnd.getTime();
    const liveSeg      = isLive && isLastSeg;
    const hrs          = (segEndDate - cursor) / 3600000;

    segs.push({
      key:      `${entry.entry_id}-${dayKey}`,
      entry_id: entry.entry_id,
      day:      dayKey,
      segStart: cursor.toISOString(),
      // For live segments we still want a representable end time for the
      // breakdown row ("8:00 PM → in progress"), so we don't blank it out.
      segEnd:   liveSeg ? null : segEndDate.toISOString(),
      segHours: Math.round(hrs * 10) / 10,
      // isPart is true if the entry crosses at least one day boundary.
      isPart:   start.toDateString() !== realEnd.toDateString(),
      isLive:   liveSeg,
    });
    cursor = nextMidnight;
  }
  return segs;
};

const computeStatus = (worked, scheduled) => {
  if (scheduled <= 0) {
    return worked > 0
      ? { label: 'Hours logged', tone: 'neutral' }
      : { label: 'Not started',  tone: 'neutral' };
  }
  const r = worked / scheduled;
  if (r < 0.7) return { label: 'Below schedule',     tone: 'low' };
  if (r < 1.0) return { label: 'On track',           tone: 'good' };
  if (r < 1.2) return { label: 'Right on schedule',  tone: 'good' };
  return         { label: 'Approaching overtime',   tone: 'warn' };
};

// Group a /me/hours response into per-day segments (only the ones inside the displayed week) and totals.
// weekTotal/dayTotals come from in-week segments only, so a 200h shift crossing weeks contributes only its in-week hours.
const summarizeWeek = (data) => {
  const map    = {};
  const totals = {};
  if (!data) return { entriesByDay: map, dayTotals: totals, weekTotal: 0 };
  const inWeekKeys = new Set((data.days || []).map(d => d.date));
  (data.entries || []).forEach(e => {
    splitEntryByDay(e).forEach(seg => {
      if (!inWeekKeys.has(seg.day)) return;       // out-of-week segment
      (map[seg.day] = map[seg.day] || []).push(seg);
      totals[seg.day] = (totals[seg.day] || 0) + seg.segHours;
    });
  });
  Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.segStart) - new Date(b.segStart)));
  Object.keys(totals).forEach(k => { totals[k] = Math.round(totals[k] * 10) / 10; });
  const week = Math.round(Object.values(totals).reduce((s, n) => s + n, 0) * 10) / 10;
  return { entriesByDay: map, dayTotals: totals, weekTotal: week };
};

const rangeLabel = (data) => {
  if (!data) return '—';
  const mon = new Date(data.weekStart + 'T00:00:00'); const sun = new Date(mon.getTime() + 6 * 24 * 3600 * 1000);
  return `${mon.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${sun.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

// ── Component ────────────────────────────────────────────────────────────────

const Timesheet = () => {
  const [weekStart, setWeekStart] = useState(getMondayISO());
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [openDay,   setOpenDay]   = useState(null);
  const [curData,   setCurData]   = useState(null);   // the current week (kept while another week is browsed)

  useEffect(() => {
    let active = true;
    setLoading(true);
    // Sprint 13.6: TZ-aware per-day bucketing — see Home.js.
    const tzOff = new Date().getTimezoneOffset();
    apiFetch(`/me/hours?weekStart=${weekStart}&tz_offset_minutes=${tzOff}`).then(({ data }) => {
      if (!active) return;
      if (data?.success) setData(data);
      setLoading(false);
    });
    return () => { active = false; };
  }, [weekStart]);

  // keep the current week's data for the top cards: reuse it when it is the week on screen, fetch it once when browsing elsewhere
  useEffect(() => {
    if (weekStart === getMondayISO()) { if (data) setCurData(data); return undefined; }
    if (curData) return undefined;
    let active = true;
    apiFetch(`/me/hours?weekStart=${getMondayISO()}&tz_offset_minutes=${new Date().getTimezoneOffset()}`).then(({ data: d }) => { if (active && d?.success) setCurData(d); });
    return () => { active = false; };
  }, [weekStart, data, curData]);

  const today      = localDayKey(new Date());
  const isThisWeek = weekStart === getMondayISO();

  const weekLabel = rangeLabel(data);

  // Group entries → segments, but only keep segments inside the displayed
  // week. weekTotal/dayTotals are computed from in-week segments only, so a
  // 200h shift crossing weeks contributes only its in-week hours here.
  const { entriesByDay, dayTotals, weekTotal } = useMemo(() => summarizeWeek(data), [data]);

  const total     = weekTotal || 0;
  const scheduled = data?.scheduledHours || 0;
  const status    = computeStatus(total, scheduled);
  const pct       = scheduled > 0 ? Math.min(100, (total / scheduled) * 100) : 0;
  const maxHours  = Math.max(8, ...Object.values(dayTotals));

  const goPrev = () => {
    const d = new Date(weekStart + 'T00:00:00');
    d.setDate(d.getDate() - 7);
    setWeekStart(localDayKey(d));
    setOpenDay(null);
  };
  const goNext = () => {
    const d = new Date(weekStart + 'T00:00:00');
    d.setDate(d.getDate() + 7);
    setWeekStart(localDayKey(d));
    setOpenDay(null);
  };
  const goThis = () => {
    setWeekStart(getMondayISO());
    setOpenDay(null);
  };

  return (
    <div className="ts-page">

      {/* Sprint 24.4 — one row: This week · Total worked (the week on screen) · Recent shifts */}
      <section className="ts-top" aria-label="Hours at a glance">
        <div className="ts-card ts-this">
          <div className="ts-card-eyebrow">This week</div>
          <div className="ts-big">{curData ? fmtHours(curData.totalHours || 0) : '—'}</div>   {/* the server's week total, exactly what the old Home card showed */}
          <div className="ts-card-meta">{rangeLabel(curData)}</div>
        </div>

        <div className="ts-card ts-total">
          <div className="ts-card-eyebrow">Total worked</div>
          <div className="ts-big">{fmtHours(total)}</div>
          <div className="ts-card-meta">
            {scheduled > 0 ? `of ${fmtHours(scheduled)} scheduled` : 'No shifts scheduled this week'}
            {weekLabel !== '—' && !isThisWeek && <> · {weekLabel}</>}
          </div>
          <div className="ts-total-foot">
            <div className={`ts-status-pill tone-${status.tone}`}>{status.label}</div>
            {scheduled > 0 && (
              <>
                <div className="ts-progress"><div className="ts-progress-fill" style={{ width: `${pct}%` }} /></div>
                <div className="ts-hero-pct">{Math.round(pct)}%</div>
              </>
            )}
          </div>
        </div>

        <div className="ts-card ts-recent">
          <div className="ts-card-eyebrow">Recent</div>
          {(curData?.recentShifts || []).length === 0 && <div className="ts-card-meta ts-recent-empty">{curData ? 'No shifts yet.' : 'Loading…'}</div>}
          <ul className="ts-recent-list">
            {(curData?.recentShifts || []).slice(0, 3).map(sh => (
              <li key={sh.entry_id}>
                <span className="ts-recent-date">{new Date(sh.clock_in_time).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                <span className="ts-recent-times">{fmtTime(sh.clock_in_time)} – {sh.clock_out_time ? fmtTime(sh.clock_out_time) : <em className="ts-entry-open">in progress</em>}</span>
                <span className="ts-recent-hours">{sh.clock_out_time ? fmtHours(sh.hours) : '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Timeline: the week being shown + its navigator, then daily totals on top and the daily breakdown below */}
      <header className="ts-timeline-head">
        <div>
          <div className="ts-eyebrow">Timeline</div>
          <h2 className="ts-week">{weekLabel}</h2>
        </div>
        <div className="ts-controls">
          <button className="ts-chev-btn" onClick={goPrev} title="Previous week" aria-label="Previous week">‹</button>
          <button className="ts-this-week-btn" onClick={goThis} disabled={isThisWeek}>This week</button>
          <button className="ts-chev-btn" onClick={goNext} disabled={isThisWeek} title="Next week" aria-label="Next week">›</button>
        </div>
      </header>

      {/* Bar chart */}
      <section className="ts-chart-card">
        <h2 className="ts-card-title">Daily totals</h2>
        <div className="ts-chart">
          {(data?.days || DAY_NAMES.map((n) => ({ date: '', dayName: n }))).map(d => {
            const h         = dayTotals[d.date] || 0;
            const heightPct = maxHours > 0 ? (h / maxHours) * 100 : 0;
            const isToday   = d.date === today;
            const isOpen    = openDay === d.date;
            return (
              <div
                key={d.date || d.dayName}
                className={`ts-chart-col${isToday ? ' is-today' : ''}${isOpen ? ' is-selected' : ''}`}
                onClick={() => d.date && setOpenDay(prev => prev === d.date ? null : d.date)}
              >
                <div className="ts-chart-bar-track">
                  <div
                    className="ts-chart-bar"
                    style={{ height: `${heightPct}%`, opacity: h ? 1 : 0.2 }}
                  />
                </div>
                <div className="ts-chart-hours">{h ? fmtHours(h) : ''}</div>
                <div className="ts-chart-day">{d.dayName}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Daily breakdown */}
      <section className="ts-daily-card">
        <h2 className="ts-card-title">Daily breakdown</h2>
        {loading && <div className="ts-loading">Loading…</div>}
        {!loading && (
          <ul className="ts-daily-list">
            {(data?.days || []).map(d => {
              const isOpen  = openDay === d.date;
              const isToday = d.date === today;
              const list    = entriesByDay[d.date] || [];
              const hasWork = list.length > 0;
              const dayHrs  = dayTotals[d.date] || 0;
              return (
                <li key={d.date} className={`ts-day${isOpen ? ' is-open' : ''}`}>
                  <button
                    className="ts-day-header"
                    onClick={() => setOpenDay(prev => prev === d.date ? null : d.date)}
                    disabled={!hasWork}
                  >
                    <span className="ts-day-chevron">{hasWork ? '›' : '·'}</span>
                    <span>
                      <span className="ts-day-name">
                        {new Date(d.date + 'T00:00:00').toLocaleDateString([], {
                          weekday: 'long', month: 'short', day: 'numeric',
                        })}
                      </span>
                      <span className="ts-day-name-meta">
                        {hasWork ? `${list.length} ${list.length === 1 ? 'entry' : 'entries'}` : 'no shifts'}
                      </span>
                    </span>
                    {isToday && <span className="ts-day-badge is-today">Today</span>}
                    <span className={`ts-day-hours${!hasWork ? ' ts-day-empty-hours' : ''}`}>
                      {hasWork ? fmtHoursMinutes(dayHrs) : '—'}
                    </span>
                  </button>

                  {isOpen && hasWork && (
                    <div className="ts-day-entries">
                      {list.map(seg => (
                        <div key={seg.key} className="ts-entry">
                          <div className="ts-entry-times">
                            {fmtTime(seg.segStart)} → {seg.isLive
                              ? <span className="ts-entry-open">in progress</span>
                              : fmtTime(seg.segEnd)}
                            {seg.isPart && <span className="ts-entry-cont">spans days</span>}
                          </div>
                          <div className="ts-entry-hours">
                            {fmtHoursMinutes(seg.segHours)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
            {!loading && (data?.days || []).length === 0 && (
              <li className="ts-loading">No data for this week.</li>
            )}
          </ul>
        )}
      </section>

    </div>
  );
};

export default Timesheet;
